/**
 * `@dxf-ai-editor/cad-core` — the MULTILEADER pieces whose value depends on
 * the ACTIVE ANNOTATIVE CONTEXT: the landing endpoint, the MTEXT-content box,
 * and the union of both across EVERY `annotativeContexts[]` representation.
 *
 * Split out of `leafBounds.ts` (which stays the barrel — `selectLeaf.ts`,
 * `grips.ts` and every external importer keep their `./leafBounds` paths) so
 * that file stays well inside the CLAUDE.md size ceiling while this
 * per-context union grows next to the two helpers it parameterises.
 *
 * WHY a union and not "the" active context: `mleaderContextDataSchema` is a
 * PER-SCALE snapshot and the renderer resolves exactly one of them
 * (`multileaderFromEntity.ts`'s `pickAnnotativeContext(entity.
 * annotativeContexts, ctx.annotationScale)`) — but the active CANNOSCALE is a
 * per-VIEWPORT property (two layout viewports can show the same annotative
 * MLEADER at two different scales at once), so a document-global,
 * viewport-agnostic bounds has no single well-defined context to resolve
 * against. It therefore covers EVERY one — the same "provably conservative,
 * never under-report" discipline `mtextLocalCorners` / `textLocalCorners`
 * (`drawing-model/wcsBoundsLeaf.ts`) already apply for TEXT/MTEXT and
 * `drawing-model`'s own MLEADER walks (`wcsBounds.ts::
 * multileaderContextLeaderPad` + `wcsBoundsBlock.ts::multileaderLeafLocal`)
 * apply for this very entity kind.
 */

import { multileaderDoglegEnd, multileaderNodeLastVertex, type MultileaderEntity } from "@dxf-ai-editor/drawing-model";

import type { Vec2 } from "./common";
import { rotateRad } from "./common";

/**
 * The LANDING segment's far endpoint — the gap-adjusted point on the
 * `doglegEnd → textLocation` line, `landingGap` units short of
 * `textLocation` (falls back to the +X direction when `doglegEnd`
 * coincides with `textLocation`, mirroring the renderer's own
 * zero-length-vector guard, `multileaderToPrimitives.ts::unit`). Paired
 * with {@link multileaderDoglegEnd} by `select.ts`'s Multileader case
 * (Codex #467 round-2 "Include rendered MLEADER dogleg segments in select
 * geometry") to reconstruct the SAME two segments the renderer draws
 * (dogleg: last-vertex → doglegEnd; landing: doglegEnd → landingEnd).
 *
 * ALSO unioned by `multileaderBounds` since Codex PR #784 round 9 P2 — for the
 * master representation and, through
 * {@link multileaderAnnotativeContextCorners}, at every annotative context's
 * own `landingGap`. The prior claim that bounds needed no separate point ("the
 * landing segment lies strictly ON the doglegEnd↔textLocation line") holds only
 * while `landingGap ≤ |textLocation − doglegEnd|`: a LONGER gap pushes
 * `textLocation − unit(textLocation − doglegEnd)·landingGap` PAST the dogleg
 * end, outside the `{doglegEnd, textLocation}` pair, and the renderer draws it
 * there. `drawing-model`'s own two MLEADER walks closed the identical hole in
 * their round-25 Fix 1 / round-26 Fix 3; `selectLeaf.ts`'s Multileader case had
 * always emitted the point, so bounds were the odd one out and select could sit
 * OUTSIDE its own entity box.
 */
export function multileaderLandingEnd(
  e: Pick<MultileaderEntity, "textLocation" | "landingGap">,
  doglegEnd: Vec2,
): Vec2 {
  const dx = e.textLocation.x - doglegEnd.x;
  const dy = e.textLocation.y - doglegEnd.y;
  const mag = Math.hypot(dx, dy);
  // Equivalent-mutant note (targeted mutation, PR #784 round 9): `<` mutated to
  // `<=` SURVIVES on both lines and is equivalent — the two predicates differ
  // ONLY at `mag === 1e-9` EXACTLY, a single point of the real line that no
  // coordinate pair reachable through `Math.hypot` of double-precision DXF
  // coordinates lands on, and at which both arms agree to within 1e-9 units
  // anyway (a nanometre in any real drawing unit). The KILLABLE `-> true` /
  // `-> false` twins are killed by the zero-length and diagonal dogleg cases in
  // `multileaderContextBounds.test.ts`, so no per-mutator `Stryker disable` is
  // used (it would hide those twins too).
  const dirX = mag < 1e-9 ? 1 : dx / mag;
  const dirY = mag < 1e-9 ? 0 : dy / mag;
  return { x: e.textLocation.x - dirX * e.landingGap, y: e.textLocation.y - dirY * e.landingGap };
}

/**
 * The 4 corners (WCS) of the multileader's embedded MTEXT-content box, centred
 * on `textLocation` and ROTATED by `textRotation` — mirrors the renderer's own
 * content box (`scene/renderMultileader.tsx`'s `contentType === 2` branch +
 * `scene3d/dimensions/multileaderToPrimitives.ts`). Local (unrotated) extents:
 *
 *   - VERTICAL half-extent = `lineCount · (textHeight · textLineSpacingFactor)`
 *     — grows with the real `\P`/newline paragraph count and line spacing
 *     (Codex #467 P2 "MLEADER text layout — rotation + multi-line spacing not
 *     bounded"). A single unrotated line reduces to `±textHeight` (the prior
 *     round-2 box), preserving that fix's behaviour.
 *   - HORIZONTAL half-extent = `max(textWidth, max(longestChars · textHeight ·
 *     0.6, textHeight)) / 2` — an explicit wrap width (group 40) may WIDEN the
 *     content-length estimate, never NARROW it. Codex PR #784 round 9 P2: the
 *     prior `textWidth > 0 ? textWidth : estimate` used the hint AS the width,
 *     under-reporting every MLEADER whose glyph run is longer than its
 *     `textWidth`, because NEITHER live renderer clips the run to it — the SVG
 *     path (`scene/renderMultileader.tsx`) uses `textWidth` only to size the
 *     optional decorative `textFrameDisplay` `<rect>` (SVG `<text>` never
 *     wraps), and the scene3d path (`geom/multileaderGeometry.ts`) carries no
 *     `textWidth` in `MultileaderRawFields` at all, feeding `raw.contents`
 *     straight to `parseMTextContent` with no wrap-width argument. The
 *     property suite's generated counterexample (`textWidth: 5`,
 *     `textHeight: 5.000002`) proved the shrink case escapes the box.
 *     `drawing-model`'s twin `wcsBoundsLeaf.ts::centeredTextBoxLocal` already
 *     applies exactly this `Math.max` (its own round-25 Fix 9) and its doc
 *     asserts the two match — this restores that.
 *
 * Symmetric-about-anchor (over-inclusive, never under-reports — the established
 * conservative-bounds convention) because MLEADER content has no group-71
 * attachment anchor of its own (`textAttachmentType`/`textAttachmentDirection`
 * is a distinct MLEADER-only system), so a plain-MTEXT `anchoredBoxCorners`
 * corner set would be mismatched. Exported so `select.ts`'s Multileader case
 * builds the SAME content box (Codex #467 P2 "MLEADER text extents missing from
 * select geometry") — one truth for bounds + select.
 */
export function multileaderTextContentCorners(e: MultileaderEntity): Vec2[] {
  const lines = e.textContents.split(/\\P|\n/g);
  const lineCount = Math.max(1, lines.length);
  const longest = lines.reduce((m, l) => Math.max(m, l.length), 0);
  const lineSpacing = e.textLineSpacingFactor > 0 ? e.textLineSpacingFactor : 1;
  const halfY = lineCount * (e.textHeight * lineSpacing);
  const contentEstimate = Math.max(longest * e.textHeight * 0.6, e.textHeight);
  // Equivalent-mutant note (targeted mutation, PR #784 round 9): the guard
  // mutated to `true` (always take the `Math.max`) and to `e.textWidth >= 0`
  // both SURVIVE and are mathematically equivalent — `Math.max(w,
  // contentEstimate) === contentEstimate` for every `w <= contentEstimate`, and
  // `contentEstimate` is `Math.max(longest·height·0.6, height) >= 0` for any
  // non-negative `textHeight` (a DXF group-40 wrap width is never negative), so
  // both branches agree on the whole `textWidth <= 0` domain the guard splits.
  // The KILLABLE `-> false` twin is the PRE-FIX under-report and is killed by
  // `multileaderContextBounds.test.ts`'s "textWidth WIDENS, never NARROWS"
  // suite (+ its property sibling), so no per-mutator `Stryker disable` is used.
  const width = e.textWidth > 0 ? Math.max(e.textWidth, contentEstimate) : contentEstimate;
  const halfX = width * 0.5;
  const local: Vec2[] = [
    { x: -halfX, y: -halfY },
    { x: halfX, y: -halfY },
    { x: halfX, y: halfY },
    { x: -halfX, y: halfY },
  ];
  return local.map((c) => {
    // GitHub Codex review PR #558 round 2 (round-6 local read-only
    // follow-up) P2 — `MultileaderEntity.textRotation` is stored in
    // RADIANS end-to-end (model ⇔ DXF, confirmed by the command-registry
    // doc + a byte-faithful CONTEXT_DATA group-42 round-trip test — see
    // `mirrorRotationField.ts::mirrorRotationFieldRadians`'s doc for the
    // full two-source evidence). `rotateRad` (this file's `rotateDeg`
    // sibling in `./common`) is the radians-native rotation, not
    // `rotateDeg` treating the raw radians number as if it were degrees
    // (a `Math.PI/2` rotation — 90° — would have bounded at ~1.57°
    // instead), which desyncs this box from the actually-rendered
    // geometry for insertion-overlap checks and block-preview framing.
    const r = rotateRad(c.x, c.y, e.textRotation);
    // Stryker disable next-line ArithmeticOperator: `local`'s 4 corners are a
    // rectangle centred on the origin — a centro-symmetric point SET
    // (`-{r_i} === {r_i}` as a set, since each corner's negation is also one
    // of the 4 corners). Rotation is linear, so `rotateRad` preserves that
    // symmetry for ANY angle. Negating the translation (`- r.x`/`- r.y`
    // instead of `+`) therefore maps the SAME 4-point set through
    // `anchor - S = anchor + (-S) = anchor + S`, producing an IDENTICAL
    // bounding box for every possible anchor/rotation/size — a mathematically
    // equivalent mutant for this bbox-only consumer, not a missed test.
    return { x: e.textLocation.x + r.x, y: e.textLocation.y + r.y };
  });
}

/**
 * Every extra point the renderer can draw for `e` under one of its
 * ANNOTATIVE CONTEXTS (Codex PR #784 round 9 P2 "Include annotative MLEADER
 * contexts in block bounds") — union'd into {@link multileaderBounds} on top
 * of the MASTER representation's own points, never replacing them.
 *
 * The substituted field list is READ OFF the render adapter, not guessed:
 * `apps/web/…/scene3d/adapters/typed/multileaderFromEntity.ts` builds its
 * `MultileaderRawFields` as `annoCtx?.<field> ?? entity.<field>` for exactly
 * `arrowheadSize`, `doglegLength`, `landingGap`, `textHeight`, `textRotation`
 * and `textColor`, and reads `entity.<field>` verbatim for everything else
 * (`leaderType`, `textMirrored`, `blockScale`/`blockRotation`,
 * `textLocation`, the leader hierarchy). Of those six, `textColor` carries no
 * geometry; the other five are covered here and in
 * {@link multileaderArrowCorners}:
 *
 *   - `arrowheadSize` — NOT re-walked here. The arrow footprint is already
 *     context-aware at its source: `multileaderArrowCorners` sizes every
 *     arrow by `max(master, …every context)`, and the arrow's tip/direction
 *     are context-INDEPENDENT (leader-line vertices are never overridden by a
 *     context), so one max-size pass already covers every context's arrow.
 *     Re-walking it per context here would be a second, parallel path for a
 *     footprint that is already bounded (round 9's own instruction).
 *   - `doglegLength` — the dogleg jogs a different distance off the same last
 *     leader vertex, so each context's own {@link multileaderDoglegEnd}
 *     endpoint is added.
 *   - `landingGap` — the landing endpoint past that dogleg
 *     ({@link multileaderLandingEnd}); with a gap LONGER than the
 *     dogleg-to-text distance it overshoots `textLocation`, outside the
 *     `{doglegEnd, textLocation}` pair. (`drawing-model`'s own round-25
 *     Fix 1 / round-26 Fix 3 closed the identical hole in its two MLEADER
 *     walks; `multileaderBounds` now adds the MASTER landing endpoint for the
 *     same reason.)
 *   - `textHeight` / `textRotation` — a taller or differently-rotated
 *     MTEXT-content box ({@link multileaderTextContentCorners}); `textWidth`
 *     rides along because the per-scale snapshot carries its own group-40
 *     value, exactly as `drawing-model/wcsBoundsBlock.ts::multileaderLeafLocal`
 *     passes `context.textWidth` to its own per-context `centeredTextBoxLocal`
 *     call. `textLineSpacingFactor` has no per-scale counterpart in
 *     `mleaderContextDataSchema`, so the master's own value stands (the same
 *     field-by-field substitution the render adapter performs).
 *
 * Returns `[]` for a non-annotative MLEADER (`annotativeContexts` empty or
 * absent) — the overwhelmingly common case: one `?? []` guard, zero loop
 * iterations, so `multileaderBounds` is byte-identical to its pre-fix result
 * for every MLEADER in a drawing without annotation scales.
 */
export function multileaderAnnotativeContextCorners(e: MultileaderEntity): Vec2[] {
  // Stryker disable next-line ArrayDeclaration: the only mutant of this empty accumulator inserts a non-point element, which every consumer (`multileaderBounds` → `boundsFromPoints`) drops through its finite-coordinate filter — observationally equivalent, the same triage `leafBounds.ts::textBounds` already carries for its own empty-array literal.
  const out: Vec2[] = [];
  // Stryker disable next-line ArrayDeclaration: the `?? []` fallback's only mutant substitutes a non-context element, whose missing `doglegLength`/`landingGap`/`textHeight` make every derived coordinate NaN — dropped by `boundsFromPoints`' finite filter downstream, so the observable box is unchanged. The KILLABLE `&&`/empty-body twins on this line are killed by the "tolerates an ABSENT `annotativeContexts` field" + "unions EVERY context" cases.
  for (const context of e.annotativeContexts ?? []) {
    // `multileaderDoglegEnd` reads `landingEnabled`/`doglegEnabled`/
    // `doglegLength`/`textLocation` — only the length differs per context, so
    // the view substitutes THAT field alone and keeps the entity's own
    // enable flags + anchor (the renderer does the same: `landingEnabled` /
    // `doglegEnabled` are read straight off the entity, never off `annoCtx`).
    const doglegView = { ...e, doglegLength: context.doglegLength };
    for (const root of e.leaderRoots) {
      for (const node of root.nodes) {
        // Context-INDEPENDENT anchor: the synthesized-landing rule reads only
        // the leader-line vertices + `textLocation`, neither of which a
        // context overrides.
        const doglegEnd = multileaderDoglegEnd(doglegView, node, multileaderNodeLastVertex(e, node));
        if (!doglegEnd) continue;
        out.push(doglegEnd);
        out.push(multileaderLandingEnd({ textLocation: e.textLocation, landingGap: context.landingGap }, doglegEnd));
      }
    }
    if (e.contentType === 2) {
      out.push(
        ...multileaderTextContentCorners({
          ...e,
          textHeight: context.textHeight,
          textRotation: context.textRotation,
          textWidth: context.textWidth,
        }),
      );
    }
  }
  return out;
}
